import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaArrowRight, FaBalanceScale, FaDatabase, FaHdd, FaLayerGroup, FaNetworkWired, FaRobot, FaServer, FaTerminal } from 'react-icons/fa';
import { RootState } from '../store/store';
import '../styles/StartPage.scss';
import TrialLock from '../components/TrialLock';

// The deploy page (/deploy). It also had an "mcp" mode for /mcp; that is McpPage now (Milos 2026-10-26).

const deploymentChoices = [
    { title: 'Virtual machine', detail: 'A Linux server you control', trialEligible: true, to: '/category/compute/instances/create', icon: FaServer },
    { title: 'Managed PostgreSQL', detail: 'A managed relational database', trialEligible: true, to: '/category/managed/databases?create=1', icon: FaDatabase },
    { title: 'Autoscaling cluster', detail: 'A self-scaling server group', to: '/category/compute/clusters?create=1', icon: FaLayerGroup },
    { title: 'MySQL or MariaDB', detail: 'Managed SQL', to: '/category/managed/mysql?create=1', icon: FaDatabase },
    { title: 'ClickHouse', detail: 'Analytics database', to: '/category/managed/clickhouse?create=1', icon: FaDatabase },
    { title: 'Redis', detail: 'Persistent cache', to: '/category/managed/redis?create=1', icon: FaDatabase },
    { title: 'Vector database', detail: 'Qdrant', to: '/category/managed/vector-db?create=1', icon: FaDatabase },
    { title: 'Private network', detail: 'Isolated networking', to: '/category/network/private-networks?create=1', icon: FaNetworkWired },
    { title: 'Block volume', detail: 'Attachable storage', to: '/category/block-storage/volumes?create=1', icon: FaHdd },
    { title: 'Load balancer', detail: 'Traffic distribution', to: '/category/network/load-balancers?create=1', icon: FaBalanceScale },
];

const StartPage: React.FC = () => {
    const forecast = useSelector((state: RootState) => state.billingForecast.forecast);
    const isLimitedTrial = forecast?.tier === 'trial_no_card';
    const cardlessFree = forecast?.policy_version === 3 && isLimitedTrial;

    const renderDeploymentChoice = ({ title, detail, to, icon, trialEligible }: typeof deploymentChoices[number], compact = false) => {
        const availability = cardlessFree
            ? 'Card required'
            : isLimitedTrial
            ? (trialEligible ? 'Available on your limited trial' : 'Requires a payment method on the limited trial')
            : null;

        return (
            <TrialLock key={to} locked={!trialEligible} paidResource><Link className={`start-resource-choice${compact ? ' start-resource-choice--compact' : ''}`} to={to} key={to}>
                <span className="start-resource-choice__icon">{React.createElement(icon)}</span>
                <div>
                    <h3>{title}</h3>
                    <p>{detail}</p>
                    {availability && <small className={trialEligible && !cardlessFree ? 'start-resource-choice__availability' : 'start-resource-choice__availability is-locked'}>{availability}</small>}
                </div>
                <FaArrowRight className="start-resource-choice__arrow" />
            </Link></TrialLock>
        );
    };

    return (
        <main className="start-page">
            <header className="start-page__header">
                <span>Deploy</span>
                <h1>Choose how to create infrastructure.</h1>
                <p>Start with an agent, or create one resource yourself.</p>
            </header>
            <section className="start-agent-callout" aria-label="Deploy with an agent">
                <FaRobot />
                <div><span>Recommended</span><h2>Deploy with an agent</h2><p>Let Claude Code or Codex plan and provision with you.</p></div>
                <Link to="/mcp">Set up your agent <FaArrowRight /></Link>
            </section>
            <section className="start-manual-deploy" aria-labelledby="manual-deploy-title">
                <div className="start-manual-deploy__heading">
                    <div>
                        <span>Or create it yourself</span>
                        <h2 id="manual-deploy-title">Common starting points</h2>
                    </div>
                </div>
                <div className="start-deployment-grid start-deployment-grid--primary">
                    {deploymentChoices.slice(0, 3).map((choice) => renderDeploymentChoice(choice))}
                </div>
            </section>
            <details className="start-more-resources">
                <summary><span>More resource types</span><small>Databases, networking, and storage</small></summary>
                <div className="start-deployment-grid start-deployment-grid--secondary">
                    {deploymentChoices.slice(3).map((choice) => renderDeploymentChoice(choice, true))}
                </div>
            </details>
            <Link className="start-page__back" to="/mcp"><FaTerminal /> Read the agent deployment guide</Link>
        </main>
    );
};

export default StartPage;
